import { formatList } from "@memorang/helpers/i18n";
import type { SectionTag } from "@memorang/ui/components/FilterSections/types";
import type { QuickStudyTab } from "@memorang/ui/components/QuickStudyModal/types";
import type { TFunction } from "i18next";
import { TAG_TYPES } from "../helpers/session-filters";
import type { AdditionalParams } from "../hooks/useSessionConfig";

export const MASTERY_SECTION_KEY = "__mastery__";

/** Sections are keyed by tag type: titles can be relabelled (proficiency override) but the type is stable. */
export const resolveSectionKey = (section: SectionTag): string =>
	section.tags[0]?.type ?? section.title;

const isSkillGridSection = (section: SectionTag) =>
	section.tags[0]?.type === TAG_TYPES.skill &&
	!section.tags.some((tag) => tag.children?.length);

/** Every filter section that renders under a CollapsibleSectionHeader; the skills grid is not one. */
export const collectCollapsibleSectionKeys = ({
	sectionTags,
	showDistributionFilters,
}: {
	sectionTags: SectionTag[] | undefined;
	showDistributionFilters: boolean;
}): string[] => [
	...(sectionTags ?? [])
		.filter((section) => !isSkillGridSection(section))
		.map(resolveSectionKey),
	...(showDistributionFilters ? [MASTERY_SECTION_KEY] : []),
];

/** All collapsed when there is more than one section; a lone section starts expanded. */
export const getInitialExpandedSections = (
	collapsibleKeys: string[],
): Set<string> => new Set(collapsibleKeys.length === 1 ? collapsibleKeys : []);

export const getInitialTab = (
	additionalParams?: Pick<
		AdditionalParams,
		"selectedTagIds" | "preferRecommendedTab"
	>,
): QuickStudyTab =>
	additionalParams?.selectedTagIds?.length &&
	!additionalParams.preferRecommendedTab
		? "custom"
		: "recommended";

type SessionSummaryInput = {
	progresses: string[];
	totalProgressCount: number;
	itemCount: number;
	selectedTopics: string[];
	availableTopics: string[];
	t: TFunction;
	language: string;
};

type MasterySummary =
	| { type: "none" | "all" }
	| { type: "selected"; masteryLevels: string; masteryCount: number };

const formatTopics = ({
	selectedTopics,
	availableTopics,
	t,
	language,
}: Pick<
	SessionSummaryInput,
	"selectedTopics" | "availableTopics" | "t" | "language"
>): string => {
	if (availableTopics.length === 1) {
		return availableTopics[0];
	}

	if (
		selectedTopics.length === 0 ||
		selectedTopics.length === availableTopics.length
	) {
		return t("sessionConfig.customSummary.allTopics", {
			count: availableTopics.length,
		});
	}

	const topicLabels =
		selectedTopics.length <= 2
			? selectedTopics
			: [
					...selectedTopics.slice(0, 2),
					t("sessionConfig.customSummary.more", {
						count: selectedTopics.length - 2,
					}),
				];

	return formatList(language, topicLabels);
};

const formatMasterySummary = ({
	progresses,
	totalProgressCount,
	t,
	language,
}: Pick<
	SessionSummaryInput,
	"progresses" | "totalProgressCount" | "t" | "language"
>): MasterySummary => {
	if (totalProgressCount === 0) {
		return { type: "none" };
	}
	const uniqueProgresses = Array.from(new Set(progresses));
	if (
		uniqueProgresses.length === 0 ||
		uniqueProgresses.length === totalProgressCount
	) {
		return { type: "all" };
	}

	const masteryLevels = formatList(
		language,
		uniqueProgresses.map((progress) =>
			t("sessionConfig.customSummary.quotedMasteryLevel", {
				masteryLevel: t(`mastery.labels.${progress}`),
			}),
		),
		{ type: "disjunction" },
	);

	return {
		type: "selected",
		masteryLevels,
		masteryCount: uniqueProgresses.length,
	};
};

export const getSessionSummary = ({
	progresses,
	totalProgressCount,
	itemCount,
	selectedTopics,
	availableTopics,
	t,
	language,
}: SessionSummaryInput): string => {
	const topics = formatTopics({
		selectedTopics,
		availableTopics,
		t,
		language,
	});
	const mastery = formatMasterySummary({
		progresses,
		totalProgressCount,
		t,
		language,
	});
	const values = { count: itemCount, topics };

	switch (mastery.type) {
		case "selected":
			return t("sessionConfig.customSummary.messageWithMastery", {
				...values,
				masteryLevels: mastery.masteryLevels,
				masteryCount: mastery.masteryCount,
			});
		case "all":
			return t("sessionConfig.customSummary.messageAllMasteryLevels", values);
		default:
			return t("sessionConfig.customSummary.message", values);
	}
};
